import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Grid, TextField, Autocomplete, Typography, Button, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, IconButton, List, ListItem, ListItemText, Divider, TablePagination, Drawer, Card, CardContent, useTheme, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions, Collapse, ListItemButton } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import apiService from '../../services/apiService';
import { getCurrentUser } from '../../services/authService';
import { format, parseISO } from 'date-fns';
import BillPrintDialog from './BillPrintDialog';
import AddCustomerDialog from './AddCustomerDialog';
import AddProductDialog from './AddProductDialog';

const filter = createFilterOptions();
const WALKING_CUSTOMER_ID = 1;

const InstantBill = () => {
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerInput, setCustomerInput] = useState('');
  const [customerBalance, setCustomerBalance] = useState('0');
  const [transactionOldBalance, setTransactionOldBalance] = useState('0');
  const [currentBillSummaryId, setCurrentBillSummaryId] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [completedExpanded, setCompletedExpanded] = useState(false);

  const [salesList, setSalesList] = useState([]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [activeBills, setActiveBills] = useState([]);

  const [form, setForm] = useState({
    salesDetailId: undefined,
    product: null,
    productInput: '',
    actualPrice: '',
    salesPrice: '',
    quantity: '',
    price: '',
    originalPrice: 0,
  });
  const [saving, setSaving] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [receivedToday, setReceivedToday] = useState('');

  const customerInputRef = useRef(null);
  const productInputRef = useRef(null);
  const salesPriceInputRef = useRef(null);
  const quantityInputRef = useRef(null);

  const user = getCurrentUser();
  const [isBillPrintDialogOpen, setBillPrintDialogOpen] = useState(false);
  const [selectedBillToPrint, setSelectedBillToPrint] = useState(null);
  const [selectedBillDetailsToPrint, setSelectedBillDetailsToPrint] = useState([]);
  const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);
  const [changeCustomerDialogOpen, setChangeCustomerDialogOpen] = useState(false);
  const [customerToChange, setCustomerToChange] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const rolesRaw = user?.roles || user?.authorities || user?.permissions || user?.role || [];
  const roles = (Array.isArray(rolesRaw) ? rolesRaw : [rolesRaw])
    .map(r => String(r?.authority || r || '').toLowerCase());
  const canDelete = roles.some(role => role === 'admin' || role === 'super_user' || role === 'role_admin' || role === 'role_super_user');

  const loadCustomers = useCallback(async () => {
    try {
      const resp = await apiService.customers.getMinDetails();
      setCustomers(Array.isArray(resp.data) ? resp.data : []);
    } catch (e) {
      setCustomers([]);
    }
  }, []);

  const loadProducts = useCallback(async () => {
    try {
      const resp = await apiService.products.list({ page: 0, size: 500 });
      const content = resp.data?.content || resp.data || [];
      setProducts(Array.isArray(content) ? content : []);
    } catch (e) {
      setProducts([]);
    }
  }, []);

  const refreshBalance = useCallback(async (cust) => {
    if (!cust?.customerId || cust.customerId === WALKING_CUSTOMER_ID) {
        setCustomerBalance('0');
        return '0';
    }
    try {
      const resp = await apiService.customers.getBalance(cust.customerId);
      const bal = resp?.data?.cumulativeBalance ?? resp?.data?.oldBalance ?? 0;
      setCustomerBalance(String(bal));
      return String(bal);
    } catch (e) {
      const fallbackBal = String(cust?.balanceAmount ?? 0);
      setCustomerBalance(fallbackBal);
      return fallbackBal;
    }
  }, []);

  const fetchSales = useCallback(async (billSummaryId) => {
    if (!billSummaryId) {
      setSalesList([]);
      setTotalElements(0);
      return;
    }
    try {
      const resp = await apiService.salesDetails.listByCustomerAndDate({
        billSummaryId,
        date: format(new Date(), 'yyyy-MM-dd'),
        sortBy: 'updateTimestamp',
        direction: 'ASC'
      });
      const content = Array.isArray(resp.data) ? resp.data : (resp.data?.content || []);
      setSalesList(content.reverse());
      setTotalElements(content.length);
    } catch (e) {
      setSalesList([]);
      setTotalElements(0);
    }
  }, []);

  const loadActiveBills = useCallback(async () => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      // Fetch both Customer and Quick bills
      const [custResp, quickResp] = await Promise.all([
        apiService.collections.getActiveBillsToday({ date: today, billTypeFlag: 'C' }),
        apiService.collections.getActiveBillsToday({ date: today, billTypeFlag: 'Q' })
      ]);

      const custBills = Array.isArray(custResp.data) ? custResp.data : [];
      const quickBills = Array.isArray(quickResp.data) ? quickResp.data : [];

      setActiveBills([...custBills, ...quickBills].sort((a, b) =>
        new Date(b.updateTimestamp) - new Date(a.updateTimestamp)
      ));
    } catch (e) {
      setActiveBills([]);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
    loadProducts();
    loadActiveBills();
  }, [loadCustomers, loadProducts, loadActiveBills]);

  // Close mobile drawer when switching to desktop view to prevent state issues
  useEffect(() => {
    if (!isMobile) {
      setMobileOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (currentBillSummaryId) {
      fetchSales(currentBillSummaryId);
    }
  }, [currentBillSummaryId, fetchSales]);

  useEffect(() => {
    if (customerInputRef.current) customerInputRef.current.focus();
  }, []);

  const onCustomerChange = (_e, val) => {
    if (val?.isAddNew) {
      setAddCustomerDialogOpen(true);
      return;
    }

    setSalesList([]);
    setPage(0);
    setSelectedCustomer(val || null);

    if (val) {
      (async () => {
        const overallBalance = await refreshBalance(val);
        // Check for existing active bill for this customer
        const existingBill = activeBills.find(b => b.customerId === val.customerId && b.isPaid !== 'Y');

        if (existingBill) {
          setCurrentBillSummaryId(existingBill.billSummaryId);
          setReceivedToday(String(existingBill.receivedAmount || ''));
          setTransactionOldBalance(String(existingBill.oldBalance || '0'));
        } else {
          setCurrentBillSummaryId(null);
          setReceivedToday('');
          setTransactionOldBalance(val.customerId === WALKING_CUSTOMER_ID ? '0' : overallBalance);
        }
      })();
      setTimeout(() => {
        if (productInputRef.current) productInputRef.current.focus();
      }, 100);
    } else {
      // Reset to initial state (Walking Customer mode essentially, but waiting for input)
      setCurrentBillSummaryId(null);
      setReceivedToday('');
      setCustomerBalance('0');
      setTransactionOldBalance('0');
    }
  };

  const handleSaveNewCustomer = async (newCustomer) => {
    await loadCustomers();
    setSelectedCustomer(newCustomer);
    setCustomerInput('');
    setCustomerBalance('0');
    setTransactionOldBalance('0');
    setCurrentBillSummaryId(null);
    setReceivedToday('');
    setSalesList([]);
    setAddCustomerDialogOpen(false);
    requestAnimationFrame(() => {
        productInputRef.current?.focus();
    });
  };

  const currentActiveBill = useMemo(() => {
    if (!currentBillSummaryId) return null;
    return activeBills.find(b => b.billSummaryId === currentBillSummaryId);
  }, [activeBills, currentBillSummaryId]);

  const isWalkingCustomer = !selectedCustomer || selectedCustomer.customerId === WALKING_CUSTOMER_ID;

  const totals = useMemo(() => {
    const sum = currentActiveBill
      ? (Number(currentActiveBill.billAmount) || 0)
      : (salesList || []).reduce((acc, r) => acc + (Number(r.price || 0) || 0), 0);

    const oldBal = isWalkingCustomer ? 0 : (Number(transactionOldBalance || 0) || 0);
    const grand = sum + oldBal;
    const rec = Number(receivedToday || 0) || 0;
    const bal = Math.max(0, grand - rec);
    return { total: sum, oldBal, grandTotal: grand, received: rec, balance: bal };
  }, [salesList, transactionOldBalance, receivedToday, currentActiveBill, isWalkingCustomer]);

  const saveReceived = async () => {
    if (!currentBillSummaryId) {
      alert('Add at least one item before receiving amount');
      return;
    }
    if (Number(receivedToday || 0) <= 0) {
      alert('Received amount must be greater than 0.');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        billSummaryId: currentBillSummaryId,
        receivedAmount: receivedToday,
        billTypeFlag: isWalkingCustomer ? 'Q' : 'C'
      };
      await apiService.customerBill.receivePayment(payload);

      await loadActiveBills();

      // Reset for next bill
      setSelectedCustomer(null);
      setCustomerInput('');
      setCurrentBillSummaryId(null);
      setSalesList([]);
      setCustomerBalance('0');
      setTransactionOldBalance('0');
      setReceivedToday('');

      setTimeout(() => {
        customerInputRef.current?.focus();
      }, 0);

    } catch (e) {
      alert('Failed to save received amount');
    } finally {
      setSaving(false);
    }
  };

  const deleteRow = async (row) => {
    if (!canDelete) return;
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await apiService.salesDetails.delete(row.salesDetailId);
      await fetchSales(currentBillSummaryId);
      await loadActiveBills();
    } catch (error) {
      console.error('Failed to delete item:', error);
      alert('Failed to delete item');
    }
  };

  const onProductChange = (_e, val) => {
    if (val?.isAddNew) {
      setAddProductDialogOpen(true);
      return;
    }
    setForm((prev) => {
      const selling = val?.sellingPrice ?? '';
      const actual = val?.actualPrice ?? '';
      const qtyNum = Number(prev.quantity || 0);
      const unit = selling !== '' ? (Number(selling) || 0) : (Number(actual) || 0);
      const computedPrice = qtyNum > 0 ? String(unit * qtyNum) : '';
      return {
        ...prev,
        product: val || null,
        actualPrice: actual,
        salesPrice: selling,
        price: computedPrice,
      };
    });
    if (val) {
      setTimeout(() => {
        if (salesPriceInputRef.current) {
          salesPriceInputRef.current.focus();
        }
      }, 100);
    }
  };

  const handleSaveNewProduct = async (newProduct) => {
    await loadProducts();
    setForm((prev) => ({
        ...prev,
        product: newProduct,
        actualPrice: newProduct.actualPrice || '',
        salesPrice: newProduct.sellingPrice || '',
    }));
    setAddProductDialogOpen(false);
    requestAnimationFrame(() => {
        salesPriceInputRef.current?.focus();
    });
  };

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const validate = () => {
    if (!form.product?.productId) {
      alert('Select a product');
      return false;
    }
    if (Number(form.price || 0) <= 0) {
      alert('Price must be greater than 0.');
      return false;
    }
    return true;
  };

  const save = async () => {
    if (!validate()) {
      setTimeout(() => {
        if (productInputRef.current) {
          try { productInputRef.current.focus(); } catch {}
        }
      }, 0);
      return;
    }
    try {
      setSaving(true);

      // Determine customer ID and Bill Type
      const customerId = selectedCustomer?.customerId || WALKING_CUSTOMER_ID;
      const billTypeFlag = (customerId === WALKING_CUSTOMER_ID) ? 'Q' : 'C';

      const payload = {
        salesDetailId: form.salesDetailId,
        productId: form.product.productId,
        customerId: customerId,
        quantity: form.quantity,
        salesPrice: form.salesPrice !== '' ? form.salesPrice : form.actualPrice,
        price: form.price,
        billTypeFlag: billTypeFlag,
      };

      // If we have a current bill summary, include it to append to the same bill
      if (currentBillSummaryId) {
          payload.billSummaryId = currentBillSummaryId;
      }

      let response;
      if (billTypeFlag === 'Q') {
          response = await apiService.quickBill.addItem(payload);
      } else {
          response = await apiService.customerBill.addItem(payload);
      }

      const updatedSalesList = response.data.salesDetails || [];
      const updatedBillSummary = response.data.billSummary || {};

      const reversedList = Array.isArray(updatedSalesList) ? updatedSalesList.reverse() : [];
      setSalesList(reversedList);
      setTotalElements(reversedList.length);

      if (updatedBillSummary.billSummaryId) {
        setCurrentBillSummaryId(updatedBillSummary.billSummaryId);
        if (!isWalkingCustomer) {
            setTransactionOldBalance(String(updatedBillSummary.oldBalance || '0'));
        }
      }

      await loadActiveBills();

      setForm({
        salesDetailId: undefined,
        product: null,
        productInput: '',
        actualPrice: '',
        salesPrice: '',
        quantity: '',
        price: '',
        originalPrice: 0,
      });
    } catch (e) {
      console.error('Failed to save:', e);
      alert('Failed to save');
    } finally {
      setSaving(false);
      setTimeout(() => {
        if (productInputRef.current) {
          try { productInputRef.current.focus(); } catch {}
        }
      }, 0);
    }
  };

  const saveAndPrint = async () => {
    if (!currentBillSummaryId) {
      alert('Add at least one item before printing');
      return;
    }
    try {
      setIsPrinting(true);

      apiService.collections.saveCollection({
        billSummaryId: currentBillSummaryId,
        isPrinted: 'Y',
      });

      const billToPrint = {
        ...currentActiveBill,
        isPrinted: 'Y',
        cumulativeBalance: totals.balance,
      };

      const salesDetailsForPrint = await apiService.salesDetails.getSalesByBillSummary(currentBillSummaryId);

      setSelectedBillToPrint(billToPrint);
      setSelectedBillDetailsToPrint(salesDetailsForPrint.data);
      setBillPrintDialogOpen(true);

      loadActiveBills();
    } catch (error) {
      console.error('Error preparing bill for print:', error);
      alert('Failed to prepare bill for printing.');
    } finally {
      setIsPrinting(false);
    }
  };

  const editRow = (row) => {
    const prod = products.find(p => p.productId === row.productId) || null;
    setForm({
      salesDetailId: row.salesDetailId,
      product: prod,
      productInput: '',
      actualPrice: prod?.actualPrice ?? '',
      salesPrice: String(row.salesPrice ?? ''),
      quantity: String(row.quantity ?? ''),
      price: String(row.price ?? ''),
      originalPrice: Number(row.price || 0),
    });
  };

  const cancelEdit = () => {
    setForm({
      salesDetailId: undefined,
      product: null,
      productInput: '',
      actualPrice: '',
      salesPrice: '',
      quantity: '',
      price: '',
      originalPrice: 0,
    });
  };

  const handleNewQuickBill = () => {
    setSelectedCustomer(null);
    setCustomerInput('');
    setCustomerBalance('0');
    setTransactionOldBalance('0');
    setCurrentBillSummaryId(null);
    setReceivedToday('');
    setSalesList([]);
    setForm({
      salesDetailId: undefined,
      product: null,
      productInput: '',
      actualPrice: '',
      salesPrice: '',
      quantity: '',
      price: '',
      originalPrice: 0,
    });
    setTimeout(() => {
        if (productInputRef.current) productInputRef.current.focus();
    }, 100);
  };

  const handleChangeCustomer = async () => {
    if (!currentBillSummaryId || !customerToChange) {
      alert('No active bill or new customer selected.');
      return;
    }
    try {
      await apiService.collections.changeCustomer(currentBillSummaryId, customerToChange.customerId);
      setChangeCustomerDialogOpen(false);
      
      // Manually fetch updated bills to ensure we have the latest data immediately
      const today = format(new Date(), 'yyyy-MM-dd');
      const [custResp, quickResp] = await Promise.all([
        apiService.collections.getActiveBillsToday({ date: today, billTypeFlag: 'C' }),
        apiService.collections.getActiveBillsToday({ date: today, billTypeFlag: 'Q' })
      ]);

      const custBills = Array.isArray(custResp.data) ? custResp.data : [];
      const quickBills = Array.isArray(quickResp.data) ? quickResp.data : [];
      const allBills = [...custBills, ...quickBills].sort((a, b) =>
        new Date(b.updateTimestamp) - new Date(a.updateTimestamp)
      );

      setActiveBills(allBills);

      // Find the updated bill to update local state
      const updatedBill = allBills.find(b => b.billSummaryId === currentBillSummaryId);

      if (updatedBill) {
          setSelectedCustomer(customerToChange);
          setTransactionOldBalance(String(updatedBill.oldBalance || '0'));
          refreshBalance(customerToChange);
      }

      setCustomerToChange(null);
    } catch (error) {
      console.error('Failed to change customer:', error);
      alert('Failed to change customer.');
    }
  };

  const headerCellSx = {
    '& .MuiTableCell-head': {
      backgroundColor: '#e3f2fd',
      fontWeight: 700,
      borderBottom: '1px solid #bbdefb'
    }
  };

  const onPickActiveBill = (item) => {
    const found = (customers || []).find(c => c.customerId === item.customerId);

    if (item.billTypeFlag === 'Q' || item.customerId === WALKING_CUSTOMER_ID) {
        setSelectedCustomer(null);
    } else if (found) {
        setSelectedCustomer(found);
        refreshBalance(found);
    }

    setSalesList([]);
    setPage(0);
    setCurrentBillSummaryId(item.billSummaryId);
    setReceivedToday(String(item.receivedAmount || ''));
    setTransactionOldBalance(String(item.oldBalance || '0'));
    fetchSales(item.billSummaryId);
  };

  const isCurrentBillPaid = currentActiveBill?.isPaid === 'Y';
  const isCurrentBillPrinted = currentActiveBill?.isPrinted === 'Y';

  const renderActiveBillsList = (isMobile = false) => {
    const pendingBills = activeBills.filter(b => b.isPaid !== 'Y');
    const completedBills = activeBills.filter(b => b.isPaid === 'Y');

    const renderBillItem = (ab, isCompleted = false) => (
        <React.Fragment key={ab.billSummaryId}>
            <ListItem
                button
                onClick={() => {
                    onPickActiveBill(ab);
                    if (isMobile) setMobileOpen(false);
                }}
                sx={{ '&:hover': { bgcolor: 'action.hover' }, pl: isCompleted ? 4 : 2 }}
            >
                <ListItemText
                    primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {ab.billTypeFlag === 'Q' ? (ab.manualBillNo || '-') : (ab.customerName || 'Unknown')}
                                {' : '}
                                {((Number(ab.billAmount) || 0) + (ab.billTypeFlag === 'C' ? (Number(ab.oldBalance) || 0) : 0)).toFixed(2)}
                                {ab.isPaid === 'Y' && <CheckCircleIcon sx={{ color: 'success.main', fontSize: '0.9rem', ml: 0.5, verticalAlign: 'middle' }} />}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', border: '1px solid', px: 0.5, borderRadius: 0.5, ml: 1 }}>
                                {ab.billTypeFlag}
                            </Typography>
                        </Box>
                    }
                    disableTypography
                    secondary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '75%' }}>
                                {ab.updateUser || ab.createdBy || ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {ab.updateTimestamp ? format(parseISO(ab.updateTimestamp), 'HH:mm') : ''}
                            </Typography>
                        </Box>
                    }
                />
            </ListItem>
            <Divider component="li" />
        </React.Fragment>
    );

    return (
        <Paper sx={{ p: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Active Bills (Today)</Typography>
                {isMobile && (
                    <IconButton onClick={() => setMobileOpen(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                )}
            </Box>
            <List dense sx={{ flex: 1, overflow: 'auto' }}>
                {pendingBills.map(ab => renderBillItem(ab))}
                {pendingBills.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>
                        No active bills
                    </Typography>
                )}

                {completedBills.length > 0 && (
                    <>
                        <Divider sx={{ my: 1 }} />
                        <ListItemButton onClick={() => setCompletedExpanded(!completedExpanded)} sx={{ bgcolor: '#e8f5e9', borderRadius: 1 }}>
                            <ListItemText primary={`Completed (${completedBills.length})`} />
                            {completedExpanded ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                        <Collapse in={completedExpanded} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                {completedBills.map(ab => renderBillItem(ab, true))}
                            </List>
                        </Collapse>
                    </>
                )}
            </List>
        </Paper>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 24px)' }}>
      <Paper sx={{ p: 2, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 900, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>Instant Bill</Typography>
            <Button 
                variant="contained" 
                size="small" 
                startIcon={<AddIcon />} 
                onClick={handleNewQuickBill}
                sx={{ minWidth: 'auto', px: 1, ml: 1 }}
            >
                New
            </Button>
            {activeBills.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setMobileOpen(true)}
                sx={{ display: { xs: 'flex', md: 'none' }, minWidth: 'auto', px: 1 }}
              >
                Active Bills
              </Button>
            )}
          </Box>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>{format(new Date(), 'PPP')}</Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1.5, width: '100%', mb: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1, flex: { xs: '1 1 auto', md: '1 1 auto' }, width: '100%' }}>
            <Box sx={{ flexGrow: 1 }}>
            <Autocomplete
              options={customers || []}
              getOptionLabel={(o) => {
                if (!o) return '';
                if (o.isAddNew) return o.customerName;
                return `${o.customerId} - ${o.customerName} - ${o.customerNameTamil} - ${o.customerCity}`;
              }}
              value={selectedCustomer}
              onChange={onCustomerChange}
              inputValue={customerInput}
              filterOptions={(options, params) => {
                const filtered = filter(options, params);
                filtered.push({
                  customerId: 'ADD_NEW', customerName: 'Add New Customer', customerNameTamil: '', customerCity: '', isAddNew: true
                });
                return filtered;
              }}
              onInputChange={(_e, v) => setCustomerInput(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Customer (Leave empty for Cash Bill)"
                  size="small"
                  autoFocus
                  inputRef={customerInputRef}
                  inputProps={{ ...params.inputProps, tabIndex: 1 }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const getLabel = (opt) => opt ? `${opt.customerId} - ${opt.customerName} - ${opt.customerNameTamil} - ${opt.customerCity}` : '';
                      const firstMatch = (customers || []).find(opt =>
                        getLabel(opt).toLowerCase().includes((customerInput || '').toLowerCase())
                      );
                      if (firstMatch) {
                        e.preventDefault();
                        onCustomerChange(null, firstMatch);
                      }
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
                    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1976d2',
                      borderWidth: '2px'
                    }
                  }}
                  fullWidth
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                if (option.isAddNew) {
                  return (
                    <li key={key} {...otherProps} style={{ borderTop: '1px solid #eee', color: '#1976d2', fontWeight: 600 }}>
                      + {option.customerName}
                    </li>
                  );
                }
                return (
                  <li key={key} {...otherProps}>
                    {`${option.customerId} - ${option.customerName} - ${option.customerNameTamil} - ${option.customerCity}`}
                  </li>
                );
              }}
              isOptionEqualToValue={(o, v) => o.customerId === v?.customerId}
            />
            </Box>

            {!isWalkingCustomer && (
                <Paper sx={{
                px: { xs: 1, md: 2 },
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: { xs: 'auto', md: '200px' },
                flexShrink: 0
                }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1976d2', whiteSpace: 'nowrap', fontSize: { xs: '1rem', md: '1.25rem' } }}>
                    <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>Balance : </Box>
                    {customerBalance}
                </Typography>
                </Paper>
            )}
            {currentBillSummaryId && currentActiveBill?.billTypeFlag === 'Q' && (
                <IconButton onClick={() => setChangeCustomerDialogOpen(true)} color="primary">
                    <EditIcon />
                </IconButton>
            )}
          </Box>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, flexGrow: 1, minHeight: 0 }}>
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {(!isMobile) && (
            <Paper sx={{ p: 2, mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>Add Product</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', rowGap: { xs: 2, sm: 1.5 }, columnGap: { xs: 1, sm: 1.5 } }}>
                <Box sx={{ flex: { xs: '1 1 calc(100% - 100px)', md: '2 1 250px' } }}>
                  <Autocomplete
                    options={products || []}
                    getOptionLabel={(o) => {
                      if (!o) return '';
                      if (o.isAddNew) return o.productName;
                      return `${o.productId} - ${o.productName} - ${o.productNameTamil || ''}`;
                    }}
                    value={form.product}
                    onChange={onProductChange}
                    inputValue={form.productInput}
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);
                      filtered.push({
                        productId: 'ADD_NEW', productName: 'Add New Product', productNameTamil: '', isAddNew: true
                      });
                      return filtered;
                    }}
                    onInputChange={(_e, v) => setField('productInput', v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Product"
                        size="small"
                        inputRef={productInputRef}
                        inputProps={{ ...params.inputProps, tabIndex: 2 }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const labelFor = (opt) => opt ? `${opt.productId} - ${opt.productName} - ${opt.productNameTamil || ''}` : '';
                            const firstMatch = (products || []).find(opt =>
                              labelFor(opt).toLowerCase().includes(String(form.productInput || '').toLowerCase())
                            );
                            if (firstMatch) {
                              e.preventDefault();
                              onProductChange(null, firstMatch);
                            }
                          }
                        }}
                        fullWidth
                      />
                    )}
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      if (option.isAddNew) {
                        return (
                          <li key={key} {...otherProps} style={{ borderTop: '1px solid #eee', color: '#1976d2', fontWeight: 600 }}>
                            + {option.productName}
                          </li>
                        );
                      }
                      return (
                        <li key={key} {...otherProps}>
                          {`${option.productId} - ${option.productName} - ${option.productNameTamil || ''}`}
                        </li>
                      );
                    }}
                    isOptionEqualToValue={(o, v) => o.productId === v?.productId}
                  />
                </Box>

                <Box sx={{ flex: { xs: '0 0 90px', sm: '1 1 100px' } }}>
                  <TextField
                    label="Actual Price"
                    size="small"
                    value={form.actualPrice}
                    InputProps={{ readOnly: true }}
                    fullWidth
                    inputProps={{ tabIndex: -1 }}
                  />
                </Box>

                <Box sx={{ flex: { xs: '1 1 85px', sm: '1 1 100px' } }}>
                  <TextField
                    label="Sales Price"
                    size="small"
                    inputRef={salesPriceInputRef}
                    value={form.salesPrice}
                    onChange={(e) => {
                      const val = e.target.value;
                      setForm(prev => {
                        const qtyNum = Number(prev.quantity || 0);
                        const unit = val === '' ? (Number(prev.actualPrice || 0) || 0) : (Number(val) || 0);
                        const computed = qtyNum > 0 ? String(unit * qtyNum) : '';
                        return { ...prev, salesPrice: val, price: computed };
                      });
                    }}
                    type="number"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (quantityInputRef.current) {
                          quantityInputRef.current.focus();
                        }
                      }
                    }}
                    inputProps={{ tabIndex: 3 }}
                    fullWidth
                  />
                </Box>

                <Box sx={{ flex: { xs: '1 1 85px', sm: '1 1 100px' } }}>
                  <TextField
                    label="KG / Count"
                    size="small"
                    inputRef={quantityInputRef}
                    value={form.quantity}
                    onChange={(e) => {
                      const val = e.target.value;
                      setForm(prev => {
                        const unit = prev.salesPrice !== '' ? (Number(prev.salesPrice) || 0) : (Number(prev.actualPrice || 0) || 0);
                        const computed = val === '' ? '' : String(unit * (Number(val) || 0));
                        return { ...prev, quantity: val, price: computed };
                      });
                    }}
                    type="number"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        save();
                      }
                    }}
                    inputProps={{ tabIndex: 4 }}
                    fullWidth
                  />
                </Box>

                <Box sx={{ flex: { xs: '1 1 85px', sm: '1 1 100px' } }}>
                  <TextField
                    label="Price"
                    size="small"
                    value={form.price}
                    InputProps={{ readOnly: true }}
                    type="number"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        save();
                      }
                    }}
                    inputProps={{ tabIndex: 5 }}
                    fullWidth
                  />
                </Box>

                <Box sx={{ flex: { xs: '1 1 auto', sm: '0 0 100px' }, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={() => save()}
                    disabled={saving}
                    fullWidth
                    sx={{ height: '40px' }}
                  >
                    {form.salesDetailId ? 'Update' : 'Save'}
                  </Button>
                  {form.salesDetailId && (
                    <IconButton onClick={cancelEdit} sx={{ ml: 1 }}>
                      <CancelIcon />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Paper>
          )}

          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {isMobile ? (
              <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, pb: 1 }}>
                <Paper sx={{ p: 2, mb: 1.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>Add Product</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', rowGap: { xs: 2, sm: 1.5 }, columnGap: { xs: 0.5, sm: 1.5 } }}>
                    <Box sx={{ flex: { xs: '1 1 calc(100% - 95px)', md: '2 1 250px' } }}>
                      <Autocomplete
                        options={products || []}
                        getOptionLabel={(o) => {
                          if (!o) return '';
                          if (o.isAddNew) return o.productName;
                          return `${o.productId} - ${o.productName} - ${o.productNameTamil || ''}`;
                        }}
                        value={form.product}
                        onChange={onProductChange}
                        inputValue={form.productInput}
                        filterOptions={(options, params) => {
                          const filtered = filter(options, params);
                          filtered.push({
                            productId: 'ADD_NEW', productName: 'Add New Product', productNameTamil: '', isAddNew: true
                          });
                          return filtered;
                        }}
                        onInputChange={(_e, v) => setField('productInput', v)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Product"
                            size="small"
                            inputRef={productInputRef}
                            inputProps={{ ...params.inputProps, tabIndex: 2 }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                const labelFor = (opt) => opt ? `${opt.productId} - ${opt.productName} - ${opt.productNameTamil || ''}` : '';
                                const firstMatch = (products || []).find(opt =>
                                  labelFor(opt).toLowerCase().includes(String(form.productInput || '').toLowerCase())
                                );
                                if (firstMatch) {
                                  e.preventDefault();
                                  onProductChange(null, firstMatch);
                                }
                              }
                            }}
                            fullWidth
                          />
                        )}
                        renderOption={(props, option) => {
                          const { key, ...otherProps } = props;
                          if (option.isAddNew) {
                            return (
                              <li key={key} {...otherProps} style={{ borderTop: '1px solid #eee', color: '#1976d2', fontWeight: 600 }}>
                                + {option.productName}
                              </li>
                            );
                          }
                          return (
                            <li key={key} {...otherProps}>
                              {`${option.productId} - ${option.productName} - ${option.productNameTamil || ''}`}
                            </li>
                          );
                        }}
                        isOptionEqualToValue={(o, v) => o.productId === v?.productId}
                      />
                    </Box>

                    <Box sx={{ flex: { xs: '0 0 90px', sm: '1 1 100px' } }}>
                      <TextField
                        label="Actual Price"
                        size="small"
                        value={form.actualPrice}
                        InputProps={{ readOnly: true }}
                        fullWidth
                        inputProps={{ tabIndex: -1 }}
                      />
                    </Box>

                    <Box sx={{ flex: { xs: '1 1 55px', sm: '1 1 100px' } }}>
                      <TextField
                        label="Sales Price"
                        size="small"
                        inputRef={salesPriceInputRef}
                        value={form.salesPrice}
                        onChange={(e) => {
                          const val = e.target.value;
                          setForm(prev => {
                            const qtyNum = Number(prev.quantity || 0);
                            const unit = val === '' ? (Number(prev.actualPrice || 0) || 0) : (Number(val) || 0);
                            const computed = qtyNum > 0 ? String(unit * qtyNum) : '';
                            return { ...prev, salesPrice: val, price: computed };
                          });
                        }}
                        type="number"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (quantityInputRef.current) {
                              quantityInputRef.current.focus();
                            }
                          }
                        }}
                        inputProps={{ tabIndex: 3 }}
                        fullWidth
                      />
                    </Box>

                    <Box sx={{ flex: { xs: '1 1 55px', sm: '1 1 100px' } }}>
                      <TextField
                        label="KG / Count"
                        size="small"
                        inputRef={quantityInputRef}
                        value={form.quantity}
                        onChange={(e) => {
                          const val = e.target.value;
                          setForm(prev => {
                            const unit = prev.salesPrice !== '' ? (Number(prev.salesPrice) || 0) : (Number(prev.actualPrice || 0) || 0);
                            const computed = val === '' ? '' : String(unit * (Number(val) || 0));
                            return { ...prev, quantity: val, price: computed };
                          });
                        }}
                        type="number"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            save();
                          }
                        }}
                        inputProps={{ tabIndex: 4 }}
                        fullWidth
                      />
                    </Box>

                    <Box sx={{ flex: { xs: '1 1 55px', sm: '1 1 100px' } }}>
                      <TextField
                        label="Price"
                        size="small"
                        value={form.price}
                        InputProps={{ readOnly: true }}
                        type="number"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            save();
                          }
                        }}
                        inputProps={{ tabIndex: 5 }}
                        fullWidth
                      />
                    </Box>

                    <Box sx={{ flex: { xs: '1 1 auto', sm: '0 0 100px' }, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={() => save()}
                        disabled={saving}
                        fullWidth
                        sx={{ height: '40px' }}
                      >
                        {form.salesDetailId ? 'Update' : 'Save'}
                      </Button>
                      {form.salesDetailId && (
                        <IconButton onClick={cancelEdit} sx={{ ml: 1 }}>
                          <CancelIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </Paper>
                {salesList.map((row, index) => {
                  const prod = (products || []).find(p => p.productId === row.productId);
                  const productName = prod?.productNameTamil || prod?.productName || '';
                  return (
                  <Paper key={row.salesDetailId} sx={{ p: 0.75, mb: 0.5, mx: 0.5, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
                        {index + 1}. {productName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1 }}>
                        {row.salesPrice || prod?.actualPrice || 'N/A'} x {row.quantity} = <b>{row.price}</b>
                      </Typography>
                    </Box>

                    {!isCurrentBillPaid ? (
                      <Box sx={{ display: 'flex', flexShrink: 0, ml: 0.5 }}>
                        <IconButton size="small" onClick={() => editRow(row)} sx={{ p: 0.5 }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        {canDelete && (
                          <IconButton size="small" color="error" onClick={() => deleteRow(row)} sx={{ p: 0.5 }}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ ml: 1 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                      </Box>
                    )}
                  </Paper>
                  );
                })}
                {salesList.length === 0 && (
                  <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 4 }}>
                    No items added yet.
                  </Typography>
                )}
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0 }}>
                <Table stickyHeader size="small">
                  {salesList.length > 0 && (
                    <TableHead sx={headerCellSx}>
                      <TableRow>
                        <TableCell>S NO</TableCell>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Sales Price</TableCell>
                        <TableCell align="right">KG/Count</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                  )}
                  <TableBody>
                    {salesList.map((row, index) => {
                      const prod = (products || []).find(p => p.productId === row.productId);
                      const productName = prod?.productNameTamil || '';
                      return (
                        <TableRow key={row.salesDetailId} hover>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{productName}</TableCell>
                          <TableCell align="right">{row.salesPrice || prod?.actualPrice || 'N/A'}</TableCell>
                          <TableCell align="right">{row.quantity}</TableCell>
                          <TableCell align="right">{row.price}</TableCell>
                          <TableCell>
                            {!isCurrentBillPaid && (
                              <>
                                <IconButton size="small" onClick={() => editRow(row)}><EditIcon /></IconButton>
                                {canDelete && (
                                  <IconButton size="small" color="error" onClick={() => deleteRow(row)}><CloseIcon /></IconButton>
                                )}
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {salesList.length > 0 && (
              <Paper sx={{ p: { xs: 1, md: 1.5 }, mt: 1, flexShrink: 0, borderTop: { xs: '1px solid #e0e0e0', md: 'none' } }}>
                {/* Desktop View */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                  {!isCurrentBillPaid ? (
                    <>
                      <Box sx={{ flex: '1 1 130px' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Page Total: {totals.total.toFixed(2)}</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 130px' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Old Balance: {totals.oldBal.toFixed(2)}</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 130px' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Grand Total: {totals.grandTotal.toFixed(2)}</Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 100px' }}>
                        <TextField
                          label="Received"
                          size="small"
                          type="number"
                          value={receivedToday}
                          onChange={(e) => setReceivedToday(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              saveReceived();
                            }
                          }}
                          fullWidth
                        />
                      </Box>
                      <Box sx={{ flex: '1 1 100px' }}>
                        <Button
                          variant="contained"
                          onClick={saveReceived}
                          fullWidth
                          size="small"
                        >
                          Save
                        </Button>
                      </Box>
                      <Box sx={{ flex: '1 1 100px', textAlign: 'right' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1976d2' }}>
                          Balance: {totals.balance.toFixed(2)}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                        Old Balance: {Number(currentActiveBill?.oldBalance || 0).toFixed(2)}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                        Received Amount: {Number(currentActiveBill?.receivedAmount || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ flex: '0 0 auto', ml: 'auto' }}>
                    <Button
                      variant="contained"
                      color={isCurrentBillPrinted ? 'info' : 'success'}
                      startIcon={<PrintIcon />}
                      onClick={saveAndPrint}
                      disabled={isPrinting}
                      size="small"
                    >
                      Print
                    </Button>
                  </Box>
                </Box>

                {/* Mobile View */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                  {!isCurrentBillPaid ? (
                    <>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                         <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.1 }}>Gr: {totals.grandTotal.toFixed(2)}</Typography>
                         <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.1 }}>Old: {totals.oldBal.toFixed(2)}</Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TextField
                          placeholder="Rcvd"
                          size="small"
                          type="number"
                          value={receivedToday}
                          onChange={(e) => setReceivedToday(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              saveReceived();
                            }
                          }}
                          sx={{ width: 70, '& .MuiInputBase-input': { p: '4px 8px', fontSize: '0.9rem' } }}
                        />
                        <IconButton
                          onClick={saveReceived}
                          color="primary"
                          size="small"
                          sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, p: 0.5, borderRadius: 1 }}
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                           <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.1 }}>Bal</Typography>
                           <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1976d2', lineHeight: 1.1 }}>{totals.balance.toFixed(2)}</Typography>
                      </Box>

                      <IconButton
                        color={isCurrentBillPrinted ? 'info' : 'success'}
                        onClick={saveAndPrint}
                        disabled={isPrinting}
                        size="small"
                        sx={{ border: '1px solid', borderRadius: 1, p: 0.5, borderColor: 'divider' }}
                      >
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                       <Box>
                          <Typography variant="caption" display="block">Old Bal: {Number(currentActiveBill?.oldBalance || 0).toFixed(2)}</Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'success.main' }}>
                            Rcvd: {Number(currentActiveBill?.receivedAmount || 0).toFixed(2)}
                          </Typography>
                       </Box>
                       <Button
                          variant="contained"
                          color={isCurrentBillPrinted ? 'info' : 'success'}
                          startIcon={<PrintIcon />}
                          onClick={saveAndPrint}
                          disabled={isPrinting}
                          size="small"
                        >
                          Print
                        </Button>
                    </Box>
                  )}
                </Box>
              </Paper>
            )}
          </Box>
        </Box>

        <Box sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          {renderActiveBillsList(false)}
        </Box>

        <Drawer
          anchor="right"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{ zIndex: 1300, '& .MuiDrawer-paper': { width: '80%', maxWidth: 300 } }}
        >
          {renderActiveBillsList(true)}
        </Drawer>
        </Box>
        {selectedBillToPrint && (
          <BillPrintDialog
            open={isBillPrintDialogOpen}
            onClose={() => setBillPrintDialogOpen(false)}
            bill={selectedBillToPrint}
            salesDetails={selectedBillDetailsToPrint}
          />
        )}

        <AddCustomerDialog
          open={addCustomerDialogOpen}
          onClose={() => setAddCustomerDialogOpen(false)}
          onSaveSuccess={handleSaveNewCustomer}
        />
        <AddProductDialog
          open={addProductDialogOpen}
          onClose={() => setAddProductDialogOpen(false)}
          onSaveSuccess={handleSaveNewProduct}
        />
        <Dialog open={changeCustomerDialogOpen} onClose={() => setChangeCustomerDialogOpen(false)} fullWidth maxWidth="xs">
            <DialogTitle>Change Customer</DialogTitle>
            <DialogContent>
                <Autocomplete
                    options={customers.filter(c => c.customerId !== WALKING_CUSTOMER_ID) || []}
                    getOptionLabel={(o) => o ? `${o.customerId} - ${o.customerName} - ${o.customerNameTamil}` : ''}
                    onChange={(_e, val) => setCustomerToChange(val)}
                    autoHighlight
                    autoSelect
                    renderInput={(params) => <TextField {...params} label="New Customer" margin="normal" autoFocus />}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setChangeCustomerDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleChangeCustomer} variant="contained">Change</Button>
            </DialogActions>
        </Dialog>
    </Box>
  );
};

export default InstantBill;
